import React from 'react';
import PropTypes from 'prop-types';
import ReactSelect, { components, createFilter } from 'react-select';

import './style.scss';
import Strings from '../../Strings';
import { ControlWithTestId, OptionWithTestId, styles } from '.';

function Multiselect(props) {
  const {
    data,
    label,
    id,
    inputId,
    placeholder,
    value,
    className,
    checkboxFirst,
    menuPositionFixed = true,
    onChange,
    searchByLabelOnly,
    disabled,
    controlShouldRenderValue = false,
    ...otherProps
  } = props;
  const normalizedValue = Array.isArray(value) ? value : [];

  const getLabel = option => (
    <React.Fragment key={`option_${option.value}_${normalizedValue.includes(option.value)}`}>
      {option.label}
      {!option.noCheckbox && (
        <input type="checkbox" defaultChecked={normalizedValue.includes(option.value)} disabled={option.isDisabled} />
      )}
    </React.Fragment>
  );

  const dataForValue =
    data?.length > 0
      ? data
          .filter(d => d.options)
          .map(d => d.options)
          .flat()
          .concat(data.filter(d => !d.options))
      : data;

  const defaultFilter = {
    ignoreCase: true,
    ignoreAccents: true,
    matchFrom: 'any',
    stringify: option => `${option.data.label} ${option.data.value}`,
    trim: true,
  };

  return (
    <>
      <label htmlFor={id} className="visuallyhidden">
        {label}
      </label>
      <ReactSelect
        id={id}
        inputId={inputId || `${id}-input`}
        className={`${className || ''} ${checkboxFirst ? 'checkboxFirst' : ''} react-select-container multi-select`}
        classNamePrefix="react-select"
        value={normalizedValue.map(v => dataForValue.find(d => d.value === v)).filter(Boolean)}
        onChange={onChange}
        options={data}
        unstyled
        styles={styles}
        placeholder={placeholder}
        menuPosition={menuPositionFixed ? 'fixed' : 'absolute'}
        menuPortalTarget={menuPositionFixed ? document.body : undefined}
        classNames={{
          menuPortal: () => `multi-select ${checkboxFirst ? 'checkboxFirst' : ''} ${className || ''}`,
        }}
        isMulti
        controlShouldRenderValue={controlShouldRenderValue}
        isClearable={false}
        hideSelectedOptions={false}
        getOptionLabel={props.getLabel || getLabel}
        components={{ Group, GroupHeading, Option: OptionWithTestId, Control: ControlWithTestId }}
        filterOption={
          searchByLabelOnly ? createFilter({ stringify: option => option.data.label }) : createFilter(defaultFilter)
        }
        isDisabled={disabled}
        {...otherProps}
      />
    </>
  );
}

Multiselect.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.array,
  data: PropTypes.array.isRequired,
  closeMenuOnSelect: PropTypes.bool,
  menuPositionFixed: PropTypes.bool,
  label: PropTypes.string,
  id: PropTypes.string,
  inputId: PropTypes.string,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  checkboxFirst: PropTypes.bool,
  searchByLabelOnly: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default Multiselect;

export const SelectedOptionsType = {
  cloud: 'cloud',
  boxes: 'boxes',
};

export function SelectedOptions(props) {
  const {
    items,
    onRemove,
    onClear,
    itemsPerRow = 2,
    type = SelectedOptionsType.boxes,
    className,
    disabled,
    withClearButton = true,
  } = props;
  return (
    <div
      className={`multiselect-selected-options ${
        type === SelectedOptionsType.boxes ? `items-per-row-${itemsPerRow}` : ''
      } ${className || ''}`}
    >
      {type === SelectedOptionsType.cloud && items && items.length > 0 && withClearButton && (
        <div className="round-button" onClick={onClear}>
          {Strings.clear}
        </div>
      )}
      {items?.map((item, index) => {
        const removeDisabled = item.disabled || disabled;
        return (
          <div
            key={`selectedOption_${item.value}_${index}`}
            className={type === SelectedOptionsType.boxes ? 'checkbox-container border' : 'option'}
            data-tooltip-content={removeDisabled ? item.tooltip : undefined}
            data-tooltip-id={removeDisabled && item.tooltip ? 'tooltip' : undefined}
          >
            <p>{item.label}</p>
            <span
              className={`remove-button-wrapper ${removeDisabled ? 'disabled' : ''}`}
              data-tooltip-content={removeDisabled ? item.tooltip : undefined}
              data-tooltip-id={removeDisabled && item.tooltip ? 'tooltip' : undefined}
            >
              <button
                type="button"
                aria-label={Strings.remove}
                className="remove-button"
                onClick={() => onRemove(item)}
                disabled={removeDisabled}
              />
            </span>
          </div>
        );
      })}
    </div>
  );
}
SelectedOptions.propTypes = {
  items: PropTypes.array,
  onRemove: PropTypes.func,
  onClear: PropTypes.func,
  itemsPerRow: PropTypes.number,
  type: PropTypes.string,
  withClearButton: PropTypes.bool,
  className: PropTypes.string,
  disabled: PropTypes.bool,
};

function Group(props) {
  const allOptionsSelected = !props.children.some(opt => !opt.props.isSelected);
  const value = props.getValue();
  let newValue = [];
  const isDisabled = props.options.every(o => o.isDisabled);

  const onClick = () => {
    if (allOptionsSelected) {
      newValue = value.filter(v => !props.options.find(o => o.value === v.value));
    } else {
      newValue = value.concat(props.options.filter(o => !value.find(v => v.value === o.value)));
    }
    props.selectProps.onChange(newValue);
  };
  return (
    <components.Group {...props} headingProps={{ ...props.headingProps, allOptionsSelected, onClick, isDisabled }} />
  );
}

const GroupHeading = ({ allOptionsSelected, isDisabled, ...props }) => {
  return (
    <div className="react-select__option header">
      <components.GroupHeading {...props} className={`${isDisabled ? 'disabled' : ''}`} />
      <input type="checkbox" checked={allOptionsSelected} onChange={props.onClick} disabled={isDisabled} />
    </div>
  );
};
